import type { Notification, User } from '@gmn-portal/backend';
import { Archive, Bell, Eye, Loader2, MailCheck } from 'lucide-react';
import { DateTime } from 'luxon';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInfiniteNotifications, useUpdateNotificationStatus } from '@/core/api/notifications';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import { TabNav } from '@/shared/components/layout/tab-nav';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/shared/utils';
import {
  canReadService,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';

const NOTIFICATIONS_PER_PAGE = 10;

type NotificationContentType = {
  title: string;
  message: string;
};

const EmptyCard = ({ message }: { message: string }) => (
  <Card>
    <CardContent className="flex min-h-[112px] items-center justify-center">
      <p className="text-sm text-muted-foreground">{message}</p>
    </CardContent>
  </Card>
);

const LoadMoreButton = ({
  isFetchingNextPage,
  onClick,
}: {
  isFetchingNextPage: boolean;
  onClick: () => void;
}) => {
  const { t } = useIntl();

  return (
    <div className="flex justify-center">
      <Button
        variant="outline"
        onClick={onClick}
        disabled={isFetchingNextPage}
        className="min-w-[200px]"
      >
        {isFetchingNextPage ? (
          <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            {t('activity.loadingMore')}
          </>
        ) : (
          t('activity.loadMore')
        )}
      </Button>
    </div>
  );
};

const getNotificationContent = (
  t: (key: string) => string,
  type: string
): NotificationContentType => {
  const titleKey = `notifications.types.${type}.title`;
  const messageKey = `notifications.types.${type}.message`;

  const title = t(titleKey);
  const message = t(messageKey);

  return {
    title: title === titleKey ? type : title,
    message: message === messageKey ? type : message,
  };
};

const useNotificationLink = (notification: Notification, user: User | null) => {
  if (!user) return '';
  const group = resolveUserGroup(user);
  const domain = resolveUserGroupDomain(user);
  const canReadOperations = group ? canReadService(group, 'operations') : false;
  const canReadContracts = group ? canReadService(group, 'contracts') : false;

  if (notification.targetCollection === 'operations' && canReadOperations) {
    const basePath = domain === 'customer' ? '/c/my-operations' : '/gmn/operations';
    return `${basePath}/${notification.targetItem}`;
  }

  if (notification.targetCollection === 'contracts' && canReadContracts) {
    const basePath =
      domain === 'customer'
        ? '/c/contract-management/contracts'
        : '/gmn/contract-management/contracts';
    return `${basePath}/${notification.targetItem}`;
  }

  if (canReadContracts) {
    return domain === 'customer'
      ? '/c/contract-management/contracts'
      : '/gmn/contract-management/contracts';
  }

  if (canReadOperations) {
    return domain === 'customer' ? '/c/my-operations' : '/gmn/operations';
  }

  return domain === 'customer' ? '/c' : '/gmn';
};

const NotificationCard = ({
  notification,
  content,
  onUpdateStatus,
  showArchiveButton,
}: {
  notification: Notification;
  content: NotificationContentType;
  onUpdateStatus: (status: 'read' | 'archived') => void;
  showArchiveButton: boolean;
}) => {
  const { t } = useIntl();
  const { locale } = useIntl();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isUnread = notification.status === 'unread';
  const link = useNotificationLink(notification, user);
  const isClickable = Boolean(link);

  const handleCardClick = () => {
    if (!link) return;
    navigate(link);
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(link);
    }
  };

  const handleActionClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  return (
    <Card
      className={cn(
        'transition-colors',
        isClickable &&
          'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
      )}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={isClickable ? 'link' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `${t('notifications.actions.view')}: ${content.title}` : undefined}
    >
      <CardHeader className="border-b-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{content.title}</CardTitle>
            </div>
            <CardDescription className="mt-1.5">{content.message}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isUnread && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(event) => {
                      handleActionClick(event);
                      onUpdateStatus('read');
                    }}
                  >
                    <MailCheck className="size-4" />
                    <span className="sr-only">{t('notifications.actions.markAsRead')}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('notifications.actions.markAsRead')}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(event) => {
                    handleActionClick(event);
                    if (link) navigate(link);
                  }}
                >
                  <Eye className="size-4" />
                  <span className="sr-only">{t('notifications.actions.view')}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('notifications.actions.view')}</TooltipContent>
            </Tooltip>
            {showArchiveButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(event) => {
                      handleActionClick(event);
                      onUpdateStatus('archived');
                    }}
                  >
                    <Archive className="size-4" />
                    <span className="sr-only">{t('notifications.actions.archive')}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('notifications.actions.archive')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {DateTime.fromISO(notification.createdAt).toLocaleString(DateTime.DATETIME_MED, {
            locale,
          })}
        </div>
      </CardHeader>
    </Card>
  );
};

export const Notifications = () => {
  const { t } = useIntl();
  const location = useLocation();
  const currentTab = location.pathname.split('/').pop() || 'unread';

  const {
    data: notifications,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteNotifications({
    sort: '-createdAt',
    where: {
      status: {
        equals: currentTab,
      },
    },
    limit: NOTIFICATIONS_PER_PAGE,
  });

  const { mutate: updateStatus } = useUpdateNotificationStatus();

  const tabs = [
    {
      value: 'unread',
      label: t('notifications.tabs.unread'),
    },
    {
      value: 'read',
      label: t('notifications.tabs.read'),
    },
    {
      value: 'archived',
      label: t('notifications.tabs.archived'),
    },
  ];

  const allNotifications = notifications?.pages.flatMap((page) => page.docs) ?? [];

  return (
    <PageContainer>
      <PageTitle
        icon={Bell}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('auth.myAccount')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>{t('common.notifications')}</SimpleBreadcrumbItem>
          </>
        }
        title={t('common.notifications')}
      />

      <TabNav tabs={tabs} basePath={location.pathname.replace(/\/(unread|read|archived)?$/, '')} />

      <div className="mt-6 space-y-4">
        {!notifications ? (
          <EmptyCard message={t('common.loading')} />
        ) : allNotifications.length === 0 ? (
          <EmptyCard message={t('notifications.empty')} />
        ) : (
          <>
            {allNotifications.map((notification) => {
              const content = getNotificationContent(
                t as unknown as (key: string) => string,
                notification.type
              );

              return (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  content={content}
                  onUpdateStatus={(status) => updateStatus({ id: notification.id, status })}
                  showArchiveButton={currentTab !== 'archived'}
                />
              );
            })}

            {hasNextPage && (
              <LoadMoreButton
                isFetchingNextPage={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              />
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};
